// components/TablaMediciones.jsx - Versión corregida
import React, { useState, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import InfoButtonWithPopover from "./InfoButtonWithPopover";


const parseLocalDate = (dateInput) => {
    if (!dateInput) return null;
    if (dateInput instanceof Date) return dateInput;
    if (typeof dateInput === 'string') {
        if (dateInput.includes('T')) {
            const dateObj = new Date(dateInput);
            const userTimezoneOffset = dateObj.getTimezoneOffset() * 60000;
            return new Date(dateObj.getTime() + userTimezoneOffset);
        }
        const parts = dateInput.split('T')[0].split('-');
        if (parts.length === 3) {
            const year = parseInt(parts[0], 10);
            const month = parseInt(parts[1], 10) - 1;
            const day = parseInt(parts[2], 10);
            return new Date(year, month, day);
        }
    }
    return new Date(dateInput);
};

const TEMPLATE_INIT = { porcentaje_avance: '', fecha_medicion: null, observaciones: '' };

const TablaMediciones = ({
    mediciones,
    onMedicionesChange,
    avanceAcumulado,
    pagosProyectos = [],
    porcentajeParcial2doPago = null,
    porcentajeParcial3erPago = null,
    esRoleSeguimiento = false
}) => {
    const [template, setTemplate] = useState(TEMPLATE_INIT);

    // Verificar si un pago está efectuado (tiene fecha_pago Y monto Y nro_instruccion)
    const isPagoEfectuado = useCallback((tipoPago) => {
        const pago = pagosProyectos.find(p => p.tipo_pago === tipoPago);
        return pago?.fecha_pago &&
            pago?.monto &&
            pago?.monto !== '' &&
            pago?.monto !== '0' &&
            pago?.nro_instruccion;
    }, [pagosProyectos]);

    const handleEliminarMedicion = useCallback((index) => {
        const pagosProyectosActuales = pagosProyectos;
        const pago2do = pagosProyectosActuales.find(p => p.tipo_pago === '2do_pago');
        const pago3er = pagosProyectosActuales.find(p => p.tipo_pago === '3er_pago');

        // Verificar si los pagos están efectuados
        const isPago2doEfectuado = pago2do?.fecha_pago &&
            pago2do?.monto &&
            pago2do?.monto !== '' &&
            pago2do?.monto !== '0' &&
            pago2do?.nro_instruccion;

        const isPago3erEfectuado = pago3er?.fecha_pago &&
            pago3er?.monto &&
            pago3er?.monto !== '' &&
            pago3er?.monto !== '0' &&
            pago3er?.nro_instruccion;

        const avancePrevio = index > 0 ? (parseFloat(mediciones[index - 1]?.porcentaje_avance) || 0) : 0;
        const nuevoAvance = avancePrevio;

        const pagosAfectados = [];

        // Validar 2do pago
        if (isPago2doEfectuado && nuevoAvance < 25) {
            pagosAfectados.push('2do Pago');
        }

        // Validar 3er pago
        if (isPago3erEfectuado && nuevoAvance < 60) {
            pagosAfectados.push('3er Pago');
        }

        // Si hay pagos efectuados que se verían afectados, NO permitir eliminar
        //QUITAMOS RESRICCION POR AHORA --> NO BORRAR ESTA NOTA
        /*if (pagosAfectados.length > 0) {
            alert(`⚠️ No se puede eliminar la medición porque deshabilitaría el ${pagosAfectados.join(' y ')} que ya fue efectuado.`);
            return;
        }*/

        // Si hay pagos NO efectuados pero que perderían su histórico, mostrar advertencia
        const perderiaHistorico2do = !isPago2doEfectuado && pago2do?.porc_pago && nuevoAvance < 25;
        const perderiaHistorico3er = !isPago3erEfectuado && pago3er?.porc_pago && nuevoAvance < 60;

        //QUITAMOS RESRICCION POR AHORA --> NO BORRAR ESTA NOTA
        /*if (perderiaHistorico2do || perderiaHistorico3er) {
            const mensaje = [];
            if (perderiaHistorico2do) mensaje.push('2do pago (se perderá el histórico y configuración)');
            if (perderiaHistorico3er) mensaje.push('3er pago (se perderá el histórico y configuración)');

            const confirmar = window.confirm(
                `⚠️ Al eliminar esta medición, el avance pasará de ${avanceAcumulado.toFixed(1)}% a ${nuevoAvance.toFixed(1)}%.\n\n` +
                `Esto afectará al ${mensaje.join(' y ')}.\n\n` +
                `¿Está seguro de que desea continuar?`
            );

            if (!confirmar) return;
        }*/

        // Proceder con la eliminación
        onMedicionesChange(prev => {
            const nuevasMediciones = prev.filter((_, i) => i !== index);
            return nuevasMediciones.map((med, i) => ({
                ...med,
                nro_medicion: i + 1
            }));
        });

        // Nota: Los efectos en FormC se encargarán de actualizar los históricos automáticamente
    }, [mediciones, avanceAcumulado, pagosProyectos, onMedicionesChange]);

    // Handler para cambiar valores de medición
    const handleMedicionChange = useCallback((index, field, value) => {
        onMedicionesChange(prev => {
            const nuevasMediciones = [...prev];
            nuevasMediciones[index] = {
                ...nuevasMediciones[index],
                [field]: value
            };
            return nuevasMediciones;
        });
    }, [onMedicionesChange]);

    // Auto-crear la primera medición al cargar un % válido en el template
    const handleTemplatePercentChange = useCallback((e) => {
        const val = e.target.value;
        if (mediciones.length === 0 && val && val !== '') {
            onMedicionesChange(prev => [...prev, {
                nro_medicion: 1,
                porcentaje_avance: val,
                fecha_medicion: null,
                observaciones: ''
            }]);
            setTemplate(TEMPLATE_INIT);
        } else {
            setTemplate(prev => ({ ...prev, porcentaje_avance: val }));
        }
    }, [mediciones.length, onMedicionesChange]);

    // Handler para agregar desde la última fila (cuando ya hay mediciones)
    const handleAgregarDesdeUltima = useCallback(() => {
        const nuevaMedicion = {
            nro_medicion: mediciones.length + 1,
            porcentaje_avance: '',
            fecha_medicion: null,
            observaciones: ''
        };
        onMedicionesChange(prev => [...prev, nuevaMedicion]);
    }, [mediciones.length, onMedicionesChange]);

    // Detectar mediciones reales sin fecha para mostrar advertencia.
    // Para role=seguimiento se omiten todas las indicaciones de fecha obligatoria:
    // por ahora el campo no es requerido para ese rol.
    const medicionesSinFecha = mediciones.filter(m => !m.fecha_medicion);
    const hayMedicionesSinFecha = !esRoleSeguimiento && medicionesSinFecha.length > 0;

    // Clases de estilo
    const inputClasses = "w-full p-2 border rounded dark:bg-gray-700 dark:text-white dark:border-gray-600 text-sm";
    const inputErrorClasses = "w-full p-2 border-2 border-red-500 rounded dark:bg-gray-700 dark:text-white text-sm bg-red-50 dark:bg-red-900/20";
    const templateRowClasses = "bg-blue-50 dark:bg-blue-900/10";

    return (
        <div className="mt-6 mb-6">
            <div className="flex justify-between items-center mb-3">
                <div className="flex flex-row items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-800 dark:text-white">
                        Avance físico de la obra
                    </h3>
                    {!esRoleSeguimiento && (
                        <InfoButtonWithPopover
                            infoText="EN ETAPA DE TESTEO: Por el momento se permite cargar mediciones sin fecha, luego estará la restricción para evitar inconsistencias"
                        />
                    )}
                </div>
            </div>

            <div>
                <div className="overflow-x-auto">
                    <table className="w-full border-collapse border border-gray-300 dark:border-gray-600">
                        <thead>
                            <tr className="bg-gray-100 dark:bg-gray-700">
                                <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200 w-24">
                                    N° Medición
                                </th>
                                <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200 w-52">
                                    Porcentaje de Avance (%)
                                </th>
                                <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200 w-52">
                                    Fecha de Medición Aprobada
                                </th>
                                <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-left text-sm font-semibold text-gray-700 dark:text-gray-200">
                                    Observaciones
                                </th>
                                <th className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center text-sm font-semibold text-gray-700 dark:text-gray-200 w-24">
                                    Acciones
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {mediciones.map((medicion, index) => {
                                const avanceSinEsta = index > 0 ? (parseFloat(mediciones[index - 1]?.porcentaje_avance) || 0) : 0;
                                const pago2do = pagosProyectos.find(p => p.tipo_pago === '2do_pago');
                                const pago3er = pagosProyectos.find(p => p.tipo_pago === '3er_pago');

                                const isProtegida =
                                    (pago2do?.fecha_pago && avanceSinEsta < 25) ||
                                    (pago3er?.fecha_pago && avanceSinEsta < 60);

                                return (
                                    <tr key={index} className={`hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors ${isProtegida ? 'bg-red-50 dark:bg-red-900/10' : ''}`}>
                                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center w-24">
                                            <span className="font-medium text-gray-800 dark:text-white">
                                                {medicion.nro_medicion}
                                            </span>
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 w-52">
                                            <input
                                                type="number"
                                                min="0"
                                                max="100"
                                                step="0.01"
                                                value={medicion.porcentaje_avance || ''}
                                                onChange={(e) => handleMedicionChange(index, 'porcentaje_avance', e.target.value)}
                                                className={inputClasses}
                                                placeholder="0-100"
                                            />
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 w-52">
                                            <DatePicker
                                                selected={parseLocalDate(medicion.fecha_medicion)}
                                                onChange={(date) => handleMedicionChange(index, 'fecha_medicion', date)}
                                                dateFormat="dd/MM/yyyy"
                                                className={!medicion.fecha_medicion && !esRoleSeguimiento ? inputErrorClasses : inputClasses}
                                                placeholderText={esRoleSeguimiento ? "Fecha" : "⚠ Fecha requerida"}
                                                showYearDropdown
                                                scrollableYearDropdown
                                                yearDropdownItemNumber={15}
                                            />
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                                            <textarea
                                                value={medicion.observaciones || ''}
                                                onChange={(e) => handleMedicionChange(index, 'observaciones', e.target.value)}
                                                className={`${inputClasses} resize-y min-h-[38px]`}
                                                placeholder="Observaciones"
                                                rows={1}
                                            />
                                        </td>
                                        <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center w-24">
                                            <button
                                                type="button"
                                                onClick={() => handleEliminarMedicion(index)}
                                                className="p-1 rounded transition-colors text-red-600 hover:text-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 text-xs"
                                                title="Eliminar medición"
                                            >
                                                🗑️
                                            </button>
                                            {index === mediciones.length - 1 && (
                                                <button
                                                    type="button"
                                                    onClick={handleAgregarDesdeUltima}
                                                    className="ml-1 p-1 rounded transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-xs"
                                                    title="Agregar medición"
                                                >
                                                    ➕
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {mediciones.length === 0 && (
                                <tr className={`${templateRowClasses} transition-colors`}>
                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center w-24">
                                        <span className="font-medium text-gray-400 dark:text-gray-500">
                                            {template.porcentaje_avance ? '1' : '+'}
                                        </span>
                                    </td>
                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 w-52">
                                        <input
                                            type="number"
                                            min="0"
                                            max="100"
                                            step="0.01"
                                            value={template.porcentaje_avance}
                                            onChange={handleTemplatePercentChange}
                                            className={inputClasses}
                                            placeholder="0-100"
                                        />
                                    </td>
                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 w-52">
                                        <DatePicker
                                            selected={template.fecha_medicion}
                                            onChange={(date) => setTemplate(prev => ({ ...prev, fecha_medicion: date }))}
                                            dateFormat="dd/MM/yyyy"
                                            className={inputClasses}
                                            placeholderText="Fecha"
                                            showYearDropdown
                                            scrollableYearDropdown
                                            yearDropdownItemNumber={15}
                                        />
                                    </td>
                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2">
                                        <textarea
                                            value={template.observaciones}
                                            onChange={(e) => setTemplate(prev => ({ ...prev, observaciones: e.target.value }))}
                                            className={`${inputClasses} resize-y min-h-[38px]`}
                                            placeholder="Observaciones"
                                            rows={1}
                                        />
                                    </td>
                                    <td className="border border-gray-300 dark:border-gray-600 px-2 py-2 text-center w-24">
                                        <button
                                            type="button"
                                            onClick={() => {
                                                onMedicionesChange(prev => [...prev, {
                                                    nro_medicion: 1,
                                                    porcentaje_avance: template.porcentaje_avance,
                                                    fecha_medicion: template.fecha_medicion,
                                                    observaciones: template.observaciones
                                                }]);
                                                setTemplate(TEMPLATE_INIT);
                                            }}
                                            className="p-1 rounded transition-colors text-blue-600 hover:text-blue-800 hover:bg-blue-100 dark:hover:bg-blue-900/30 text-xs"
                                            title="Agregar medición"
                                        >
                                            ➕
                                        </button>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>

                    {/* Banner de advertencia si alguna medición no tiene fecha */}
                    {hayMedicionesSinFecha && (
                        <div className="mt-2 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-300 dark:border-amber-700 rounded-md flex items-start gap-2">
                            <span className="text-amber-600 dark:text-amber-400 text-lg leading-none mt-0.5">⚠️</span>
                            <p className="text-sm text-amber-700 dark:text-amber-300">
                                <strong>Fecha requerida:</strong> {medicionesSinFecha.length === 1
                                    ? `La Medición N° ${medicionesSinFecha[0].nro_medicion} no tiene fecha.`
                                    : `Las Mediciones N° ${medicionesSinFecha.map(m => m.nro_medicion).join(', ')} no tienen fecha.`
                                } Completá la fecha antes de guardar.
                            </p>
                        </div>
                    )}
                    </div>
            </div>
        </div>
    );
};

export default TablaMediciones;